import { useMutation, useQueryClient } from '@tanstack/react-query'

import { useAuthInfo } from '@sweetauth/core'

import { API_URL } from '@/constants/env'

import { convertDatesInObject } from '@/helpers/api'
import { SweetspotError } from '@/helpers/error'

import { useHandleError } from '@/hooks/common/useErrorHandler'
import { documentsQueryKeys } from '@/hooks/documents/types'

import type { UUID } from '@/types/Id'
import type { Document } from '@/types/pursuit/Document'

const openWorkingCopy = async (
  fileId: UUID,
  accessToken: string | undefined | null,
): Promise<Document> => {
  if (accessToken === null || accessToken === undefined) {
    throw new Error('No access token provided.')
  }
  const res: Response = await fetch(
    `${API_URL}/api/v1/files/${fileId}/working-copy`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    },
  )
  if (!res.ok) {
    throw new SweetspotError({
      name: 'FETCH_ERROR',
      message: `Failed to open file in the editor: ${res.statusText}`,
      cause: res,
    })
  }
  return convertDatesInObject(await res.json())
}

/**
 * The document a stored Word file opens as in the editor.
 *
 * The server makes it on the file's first open and hands the same one back on
 * every open after that, so a caller never has to know whether this is the
 * first: it asks, and navigates to whatever comes back.
 */
export const useOpenWorkingCopy = () => {
  const user = useAuthInfo()
  const queryClient = useQueryClient()
  const { handleError } = useHandleError()

  return useMutation<Document, Error, UUID>({
    mutationFn: (fileId) => openWorkingCopy(fileId, user?.accessToken),
    onError: (err) => {
      handleError(err)
    },
    onSuccess: (document) => {
      // A first open adds a document to the pursuit's list. A repeat open
      // changes nothing, and refetching then is cheap.
      return queryClient.invalidateQueries({
        queryKey: documentsQueryKeys.pursuit({
          pursuitId: document.pursuit_id,
        }),
      })
    },
  })
}
